/**
 * The touch pad (integration plan slice 4), lifted from the dungeon-floors
 * prototype's `#pad` (`index.html`, `dungeon-main.js`): a 3×3 grid of eight
 * directions around a centre `wait`, bottom left, and the actions, bottom
 * right: a one-shot `turn` toggle (the next direction turns instead of
 * moving, like Shift on a keyboard), `attack` and `descend`. A pressed
 * direction sends its command at once and, while held, again every
 * `uiTiming.padRepeatMs` once the last one has played (`ready`): a repeat is
 * never queued behind a busy one. Every button, the toggle included, calls
 * `onInteract` first when pressed (the shell stops a running route on it). The
 * toggle disarms on any other input: a key, or a pointer pressed anywhere but
 * on a direction (a swipe or a tap on the canvas, another pad button).
 * Every button is at least 44 CSS pixels and
 * has an aria label from a `ui.pad.*` key; the arrows are CSS, not text; the
 * town's centre shows `ui.pad.action_glyph` (○: a locale may choose its own
 * mark). Every button is the retro kit's window (`.ab-window`).
 * The component builds commands; whether one is legal is game-core's call.
 */
import { useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import type { Command, Dir } from '@ab/game-core';
import type { Strings } from '@ab/session';
import { text } from './text.ts';
import { uiTiming } from './timing.ts';
import './ui.css';
import './pad.css';

export type DirectionPadProps = {
  readonly strings: Strings;
  /** Sends one command (the shell sends it after a running route's step in progress). */
  readonly onCommand: (command: Command) => void;
  /** Called first on every button press, the turn toggle's included (the shell stops a running route). */
  readonly onInteract: () => void;
  /** True when a held button may repeat now (nothing is playing). */
  readonly ready: () => boolean;
  /**
   * `step` (the dungeon's, the default): a press sends one command and a hold repeats it. `continuous` (a walkable
   * scene, slice 6a): a direction held is a direction walked (`onHold(dir)` as it is pressed, `onHold(null)` as it is
   * released); the centre is the action (`onAction`, `ui.pad.action`, slice 6b) and the action buttons (turn,
   * attack, descend) are hidden.
   */
  readonly mode?: 'step' | 'continuous';
  readonly onHold?: (dir: Dir | null) => void;
  /** The centre of a continuous pad: talk, read, enter, confirm (slice 6b). */
  readonly onAction?: () => void;
};

/** The grid, row by row from the top: screen up is north (the camera looks north). */
const grid: readonly (Dir | 'wait')[] = ['nw', 'n', 'ne', 'w', 'wait', 'e', 'sw', 's', 'se'];

export const DirectionPad = ({ strings, onCommand, onInteract, ready, mode = 'step', onHold, onAction }: DirectionPadProps) => {
  const continuous = mode === 'continuous';
  const [turning, setTurning] = useState(false);
  const toggle = useRef<HTMLButtonElement>(null);
  const directions = useRef<HTMLDivElement>(null);
  // Armed, the toggle waits for a direction; any other input (a key, a pointer anywhere else) disarms it.
  useEffect(() => {
    if (!turning) return undefined;
    const disarm = (e: Event): void => {
      // The toggle itself and the directions act on their own press.
      const target = e.target instanceof Element ? e.target : null;
      const own = target !== null && (toggle.current?.contains(target) === true || (directions.current?.contains(target) === true && target.closest('.ab-pad-dir') !== null));
      if (!own) setTurning(false);
    };
    window.addEventListener('keydown', disarm, true);
    window.addEventListener('pointerdown', disarm, true);
    return () => {
      window.removeEventListener('keydown', disarm, true);
      window.removeEventListener('pointerdown', disarm, true);
    };
  }, [turning]);
  const held = useRef<{ readonly dir: Dir; readonly pointer: number; last: number; frame: number } | null>(null);
  const stop = (): void => {
    if (held.current !== null) {
      cancelAnimationFrame(held.current.frame);
      if (continuous) onHold?.(null);
    }
    held.current = null;
  };
  useEffect(() => stop, []);
  const repeat = (time: number): void => {
    const h = held.current;
    if (h === null) return;
    if (time - h.last >= uiTiming.padRepeatMs && ready()) {
      h.last = time;
      onCommand({ type: 'move', dir: h.dir });
    }
    h.frame = requestAnimationFrame(repeat);
  };
  const press = (dir: Dir, e: ReactPointerEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    onInteract();
    stop();
    if (continuous) {
      e.currentTarget.setPointerCapture(e.pointerId);
      held.current = { dir, pointer: e.pointerId, last: performance.now(), frame: 0 };
      onHold?.(dir);
      return;
    }
    if (turning) {
      setTurning(false);
      onCommand({ type: 'turn', dir });
      return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    onCommand({ type: 'move', dir });
    held.current = { dir, pointer: e.pointerId, last: performance.now(), frame: requestAnimationFrame(repeat) };
  };
  const release = (e: ReactPointerEvent<HTMLButtonElement>): void => {
    if (held.current?.pointer === e.pointerId) stop();
  };
  /** A button pressed from the keyboard (a click with no pointer: `detail` 0); pointer presses act on pointerdown. */
  const keyed =
    (act: () => void) =>
    (e: { readonly detail: number }): void => {
      if (e.detail !== 0) return;
      onInteract();
      act();
    };
  const send = (command: Command) => (): void => {
    onCommand(command);
  };
  const tap = (command: Command) => (e: ReactPointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onInteract();
    stop();
    onCommand(command);
  };
  const toggleTurn = (e: ReactPointerEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    onInteract();
    setTurning((t) => !t);
  };
  return (
    <>
      <div ref={directions} className="ab-pad" role="group" aria-label={text(strings, 'ui.pad.title')} data-testid="pad">
        {grid.map((cell) =>
          cell === 'wait' && continuous ? (
            <button
              key={cell}
              type="button"
              className="ab-window ab-pad-button ab-pad-action"
              data-cmd="action"
              aria-label={text(strings, 'ui.pad.action')}
              onPointerDown={(e) => {
                e.preventDefault();
                onInteract();
                stop();
                onAction?.();
              }}
              onClick={keyed(() => onAction?.())}
            >
              <span className="ab-pad-glyph" aria-hidden="true">
                {text(strings, 'ui.pad.action_glyph')}
              </span>
            </button>
          ) : cell === 'wait' ? (
            <button key={cell} type="button" className="ab-window ab-pad-button ab-pad-wait" data-cmd="wait" aria-label={text(strings, 'ui.pad.wait')} onPointerDown={tap({ type: 'wait' })} onClick={keyed(send({ type: 'wait' }))} />
          ) : (
            <button
              key={cell}
              type="button"
              className="ab-window ab-pad-button ab-pad-dir"
              data-dir={cell}
              aria-label={text(strings, turning ? `ui.pad.turn_${cell}` : `ui.pad.move_${cell}`)}
              onPointerDown={(e) => {
                press(cell, e);
              }}
              onPointerUp={release}
              onPointerCancel={release}
              onLostPointerCapture={release}
              onClick={keyed(() => {
                // A keyboard press of a held-direction pad has nothing to hold (keys walk the scene themselves).
                if (continuous) return;
                setTurning(false);
                onCommand(turning ? { type: 'turn', dir: cell } : { type: 'move', dir: cell });
              })}
            />
          ),
        )}
      </div>
      <div hidden={continuous} className="ab-pad-actions" role="group" aria-label={text(strings, 'ui.pad.actions')} data-testid="pad-actions">
        <button ref={toggle} type="button" className="ab-window ab-pad-button ab-pad-toggle" data-cmd="turn" aria-label={text(strings, 'ui.pad.turn')} aria-pressed={turning} onPointerDown={toggleTurn} onClick={keyed(() => {
            setTurning((t) => !t);
          })}>
          {text(strings, 'ui.pad.turn')}
        </button>
        <button type="button" className="ab-window ab-pad-button" data-cmd="attack" aria-label={text(strings, 'ui.pad.attack')} onPointerDown={tap({ type: 'attack' })} onClick={keyed(send({ type: 'attack' }))}>
          {text(strings, 'ui.pad.attack')}
        </button>
        <button type="button" className="ab-window ab-pad-button" data-cmd="descend" aria-label={text(strings, 'ui.pad.descend')} onPointerDown={tap({ type: 'descend' })} onClick={keyed(send({ type: 'descend' }))}>
          {text(strings, 'ui.pad.descend')}
        </button>
      </div>
    </>
  );
};
